import React, { useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { useHistory } from 'react-router-dom';
import { Badge } from 'primereact/badge';
import { InputSwitch } from 'primereact/inputswitch';
import { OverlayPanel } from 'primereact/overlaypanel';

import '../../../components/styles/style.css';
import { REACT_APP_PF_ADMIN_ID } from '../../../constants/envConstants';
import { CLIENT_ID } from '../../../constants/client.constant';

import PageFeature from './PageFeature';
import { PageHeaderBreadcrumbProps } from './interface/pageHeader.interface';

const getStatusSeverity = (status: string) => {
  switch (status) {
    case 'Open':
      return 'info';
    case 'Paid':
      return 'success';
    case 'Partial paid':
      return 'warning';
    case 'Cancelled':
      return 'danger';
    case 'Refunded':
      return 'secondary';
    default:
      return null;
  }
};

const PageHeaderBreadcrumb: React.FC<PageHeaderBreadcrumbProps> = ({
  headerValue,
  breadcrumbs,
  buttonArray,
  leadingActions,
  identifier,
  icon,
  badgeStatus,
  rightContent,
}) => {
  const clientId = localStorage.getItem('client_id');
  const history = useHistory();
  const overlayRef = useRef<OverlayPanel | null>(null);
  const [overlayContent, setOverlayContent] = useState('');
  const [overlayStyle, setOverlayStyle] = useState<React.CSSProperties>({});

  const showOverlay = (
    event:
      | React.MouseEvent<HTMLSpanElement>
      | React.FocusEvent<HTMLSpanElement>,
    text: string,
    style: React.CSSProperties = {}
  ) => {
    if (!text) return;
    setOverlayContent(text);
    setOverlayStyle(style);
    overlayRef.current?.show(event, event.currentTarget);
  };

  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleLeave = () => {
    hideTimer.current = setTimeout(() => {
      overlayRef.current?.hide();
    }, 200);
  };

  const getAction = () => {
    if (Array.isArray(buttonArray)) {
      const allActions = [];
      const buttonArrayLen = buttonArray?.length;
      for (let i = 0; i < buttonArrayLen; i++) {
        const eachBtn = buttonArray[i];
        const safeTooltip =
          typeof eachBtn?.tooltip === 'string' ? eachBtn.tooltip : '';
        if (
          ('visible' in eachBtn && !!eachBtn?.visible) ||
          !('visible' in eachBtn)
        ) {
          switch (eachBtn.type) {
            case 'switch':
              allActions.push(
                <span className="flex align-items-center gap-1">
                  {eachBtn.label}
                  <InputSwitch
                    id="active"
                    checked={Boolean(eachBtn.value)}
                    onChange={eachBtn.action}
                    disabled={eachBtn.disabled}
                  />
                </span>
              );
              break;
            default:
              allActions.push(
                <span
                  key={i}
                  className="inline-flex header-button-tooltip"
                  onMouseEnter={event =>
                    showOverlay(
                      event,
                      safeTooltip,
                      eachBtn?.tooltipOptions?.style || {}
                    )
                  }
                  onMouseLeave={handleLeave}
                  onFocus={event =>
                    showOverlay(
                      event,
                      safeTooltip,
                      eachBtn?.tooltipOptions?.style || {}
                    )
                  }
                  onBlur={handleLeave}
                >
                  <Button
                    label={eachBtn.label}
                    icon={eachBtn.icon}
                    size={eachBtn.size || 'small'}
                    outlined={eachBtn.outlined}
                    onClick={eachBtn.action}
                    disabled={eachBtn.disabled}
                    type={eachBtn?.type || 'button'}
                    loading={eachBtn?.loading}
                    className={`border-round-md py-2`}
                  />
                </span>
              );
          }
        }
      }
      return allActions;
    }
    return null;
  };
  return (
    <div
      className="col-12 mb-2 border-round-lg border-300 border-1 p-2"
      style={{
        background:
          'linear-gradient(90deg, #eff6ff 0%, #f5f3ff 50%, #fdf2f8 100%)',
      }}
    >
      <div className="flex flex-wrap justify-content-between align-items-center gap-2">
        <div className="flex flex-column gap-1 flex-grow-1 min-w-full md:min-w-0">
          <div className="flex align-items-center flex-wrap gap-2">
            {identifier &&
              (clientId === REACT_APP_PF_ADMIN_ID ||
                clientId === CLIENT_ID.PF_VALIDATION) && (
                <PageFeature identifier={identifier} />
              )}

            <span className="text-lg text-900 font-semibold">
              {headerValue}
            </span>

            {badgeStatus && (
              <Badge
                value={badgeStatus}
                severity={getStatusSeverity(badgeStatus)}
                className="px-2 text-sm font-medium"
              />
            )}

            {icon && icon}
          </div>

          <div className="flex align-items-center gap-2 flex-wrap">
            {breadcrumbs?.map((item, index) => {
              const isFirst = index === 0;
              const isLast = index === breadcrumbs.length - 1;

              const textColor = isFirst
                ? 'text-blue-600'
                : isLast
                  ? 'text-pink-500'
                  : 'text-indigo-500';

              const iconColor = isFirst
                ? 'text-indigo-400'
                : index === breadcrumbs.length - 2
                  ? 'text-pink-500'
                  : 'text-indigo-400';

              return (
                <React.Fragment key={index}>
                  {item?.icon &&
                    (typeof item.icon === 'string' ? (
                      <i className={`${item.icon} ${textColor}`} />
                    ) : (
                      <item.icon
                        size={16}
                        className={textColor}
                        strokeWidth={1.8}
                      />
                    ))}

                  <span
                    className={`cursor-pointer ${textColor}`}
                    onClick={() =>
                      item?.url && history.push(item.url, item.state)
                    }
                  >
                    {item.label}
                  </span>

                  {!isLast && (
                    <i
                      className={`pi pi-angle-right ${iconColor}`}
                      style={{ fontSize: '0.9rem' }}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        <div className="flex align-items-center gap-2 flex-wrap w-full md:w-auto">
          {rightContent ?? leadingActions}
          {/* {leadingActions} */}
          {getAction()}
        </div>
      </div>
      <OverlayPanel
        ref={overlayRef}
        dismissable={false}
        showCloseIcon={false}
        style={overlayStyle}
        onMouseEnter={() => {
          if (hideTimer.current !== null) {
            clearTimeout(hideTimer.current);
          }
        }}
        onMouseLeave={() => overlayRef.current?.hide()}
      >
        <div
          className="text-sm preline-tight"
          style={{
            maxHeight: '300px',
            overflowY: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {overlayContent}
        </div>
      </OverlayPanel>
    </div>
  );
};

export default PageHeaderBreadcrumb;
